import React, { useEffect, useState } from 'react';
import { Button, Modal, Upload } from 'antd';
import PropTypes from 'prop-types';
import { mostrarErro } from '@4beefopag/4bee-fopag-core/dist/molecules.es';
import { getWorkbook } from '../../../services/util/spreadsheet/spreadsheetReader';
import ModalSelecionarTabExcel from '../../pages/ApontamentoProducao/ModalSelecionarTabExcel';
import { importarFile } from '../../../services/apontamentoProducao';
import { useImportErrorHandler } from '../../../services/apontamentoProducao/hooks/useImportErrorHandler';

export default function ImportarPlanilha({ headers, onUploadComplete, onBeforeUploadModal }) {
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedFile, setSelectedFile] = useState();
  const [selectedSheetIndex, setSelectedSheetIndex] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const { handleImportError } = useImportErrorHandler();

  const displayModal = sheetNames.length > 1 && !selectedSheetIndex;

  const clearState = () => {
    setSheetNames([]);
    setSelectedFile(undefined);
    setSelectedSheetIndex(undefined);
  };

  const onBeforeUpload = async (file) => {
    const cancelarImportacao = await onBeforeUploadModal();

    if (!cancelarImportacao) {
      setSelectedFile(file);
    }

    return false;
  };

  const upload = async () => {
    const sheetIndex = selectedSheetIndex || 0;
    try {
      setIsLoading(true);

      const response = await importarFile({
        ...headers,
        arquivo: selectedFile,
        sheetIndex,
      });

      // Sucesso: chama callback com resposta
      await onUploadComplete(response, selectedFile, sheetIndex);
    } catch (error) {
      // Trata erros 422 (validação) de forma estruturada
      if (error.response?.status === 422) {
        const result = await handleImportError(error, selectedFile, sheetIndex);

        // Se result não é null, significa que há tratamento especial: abrir modal declarativo no pai
        if (result) {
          await onUploadComplete({ data: result }, selectedFile, sheetIndex);
        }
        // Se result é null, usuário cancelou ou erro foi tratado com modal (não precisa fazer nada)
        return;
      }

      // Outros erros (500, network, etc) são tratados genericamente
      mostrarErro(error || {});
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (selectedFile) {
        try {
          const workbook = await getWorkbook(selectedFile);
          const sheets = workbook.getSheetNames();
          if (sheets.length > 1) {
            setSheetNames(sheets);
          } else {
            await upload();
            clearState();
          }
        } catch (error) {
          clearState();
          let msg = 'Algo de errado aconteceu, por favor tente novamente.';
          if (
            error.message.includes('password-protected') ||
            error.message.includes('Encryption Flags/AlgID mismatch')
          ) {
            msg = 'O seu arquivo está protegido por senha. Remova a senha e importe novamente.';
          }

          Modal.error({ content: msg });
        }
      }
    })();
  }, [selectedFile]);

  useEffect(() => {
    (async () => {
      if (selectedSheetIndex >= 0) {
        await upload();
        clearState();
      }
    })();
  }, [selectedSheetIndex]);

  return (
    <>
      {displayModal && (
        <ModalSelecionarTabExcel
          sheetNames={sheetNames}
          onOkClick={(index) => setSelectedSheetIndex(index)}
          onCancelClick={clearState}
        />
      )}
      <Upload
        name="file"
        showUploadList={false}
        beforeUpload={onBeforeUpload}
      >
        <Button
          fontSize="12px"
          height="16px"
          color="var(--text-grey-8)"
          loading={isLoading}
        >
          Importar Excel
        </Button>
      </Upload>
    </>
  );
}

ImportarPlanilha.propTypes = {
  headers: PropTypes.shape({
    idRequisicao: PropTypes.number.isRequired,
    tipo: PropTypes.string.isRequired,
    idFolha: PropTypes.number.isRequired,
    campos: PropTypes.arrayOf(PropTypes.shape(PropTypes.any)),
    validarCampos: PropTypes.bool.isRequired,
    sheetIndex: PropTypes.number,
  }).isRequired,
  onBeforeUploadModal: PropTypes.func.isRequired,
  onUploadComplete: PropTypes.func.isRequired,
};
